import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import Fuse from 'fuse.js'
import type { IFuseOptions } from 'fuse.js'
import {
  macrosMe,
  MacrosMeListResponse,
  MacrosMeCreateBodyParams,
  MacrosMePartialUpdateBodyParams,
} from '@fireflyhealth/api'

import { useMutation, useQuery } from '~/shared/hooks/query'
export type TextMacro = MacrosMeListResponse[number]

// https://www.fusejs.io/api/options.html
const FUSE_CONFIG: IFuseOptions<TextMacro> = {
  shouldSort: true,
  threshold: 0.6,
  location: 0,
  distance: 100,
  minMatchCharLength: 1,
  keys: ['name'],
}

interface MacrosContextValue {
  macros: TextMacro[]
  isLoading: boolean
  error: unknown
  search: (token: string) => TextMacro[]
}

const MacrosContext = createContext<MacrosContextValue>({
  macros: [],
  isLoading: false,
  error: null,
  search: () => [],
})

const useMeId = () => useSelector(state => state.me?.id ?? null)

const macrosQueryKey = (ownerId: number | null) =>
  macrosMe.listQueryKey({ owner: ownerId ?? undefined })

export const MacrosProvider: FC<PropsWithChildren> = ({ children }) => {
  const meId = useMeId()

  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery<MacrosMeListResponse>(
    macrosQueryKey(meId),
    async () => {
      const response = await macrosMe.list({ params: { owner: meId ?? undefined } })
      return response.data
    },
    { enabled: meId != null },
    { error: 'Failed to fetch macros' },
  )

  // `data ?? []` would be a new array on every render while the query is empty, which would
  // rebuild the Fuse index each time.
  const macrosList = useMemo(() => data ?? [], [data])
  const fuse = useMemo(() => new Fuse(macrosList, FUSE_CONFIG), [macrosList])
  // fuse 7 returns `{ item, refIndex, score }` wrappers; callers want the macros.
  const search = useCallback(
    (token: string) => fuse.search(token).map(result => result.item),
    [fuse],
  )

  return (
    <MacrosContext.Provider value={{ macros: macrosList, isLoading, error, search }}>
      {children}
    </MacrosContext.Provider>
  )
}

export const useMacros = () => useContext(MacrosContext)

export const useCreateMacro = () => {
  const queryClient = useQueryClient()
  const meId = useMeId()
  return useMutation(
    async (body: MacrosMeCreateBodyParams) => {
      const response = await macrosMe.create({ data: body })
      return response.data
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: macrosQueryKey(meId) })
      },
    },
    { error: 'Failed to create macro' },
  )
}

export const useUpdateMacro = () => {
  const queryClient = useQueryClient()
  const meId = useMeId()
  return useMutation(
    async (payload: { id: number } & MacrosMePartialUpdateBodyParams) => {
      const { id, ...body } = payload
      const response = await macrosMe.partialUpdate({ id }, { data: body })
      return response.data
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: macrosQueryKey(meId) })
      },
    },
    { error: 'Failed to update macro' },
  )
}

export const useDeleteMacro = () => {
  const queryClient = useQueryClient()
  const meId = useMeId()
  return useMutation(
    async (id: number) => {
      await macrosMe.destroy({ id })
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: macrosQueryKey(meId) })
      },
    },
    { error: 'Failed to delete macro' },
  )
}
